import {
  Box,
  Container,
  Step,
  StepLabel,
  Stepper,
  Typography,
  Paper,
} from "@mui/material";
import { Breadcrumb } from "components/Breadcrumb";
import { BREADCRUMB_PAGE } from "components/Breadcrumb/constants";
import { FC, ReactNode } from "react";

/**
 * Props for the OnboardingStepper component
 */
type OnboardingStepperProps = {
  /** Onboarding steps */
  steps: {
    label: string;
    component: ReactNode;
    onClickStep?: () => void;
  }[];
  /** Current active step (index) */
  activeStep: number;
  /** Main onboarding title */
  title?: string;
};

/**
 * Custom stepper component for onboarding that follows the specific design
 */
export const OnboardingStepper: FC<OnboardingStepperProps> = ({
  steps,
  activeStep,
  title = "Welcome to VetNebula",
}) => {
  return (
    <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
      <Box sx={{ my: 2 }}>
        <Breadcrumb page={BREADCRUMB_PAGE.ONBOARDING} />
      </Box>
      <Box sx={{ py: 2 }}>
        {/* Header Section */}
        <Box sx={{ textAlign: "center", mb: { xs: 3, sm: 4 } }}>
          <Typography
            variant="h4"
            component="h1"
            gutterBottom
            sx={{
              fontSize: { xs: "1.75rem", sm: "2.125rem" },
              fontWeight: 600,
            }}
          >
            {title}
          </Typography>
        </Box>

        {/* Stepper Section */}
        <Box sx={{ mb: { xs: 3, sm: 4 } }}>
          <Stepper
            activeStep={activeStep}
            orientation="horizontal"
            alternativeLabel
            sx={{
              "& .MuiStepLabel-root": {
                cursor: "default",
              },
              "& .MuiStepLabel-label": {
                fontSize: { xs: "0.75rem", sm: "0.875rem" },
                fontWeight: (theme) =>
                  activeStep === 0
                    ? theme.typography.fontWeightMedium
                    : theme.typography.fontWeightRegular,
                marginTop: { xs: "4px", sm: "8px" },
              },
              "& .MuiStepConnector-line": {
                borderTopWidth: 2,
              },
              "& .MuiStep-root": {
                padding: { xs: "0 4px", sm: "0 8px" },
              },
              "& .MuiStepIcon-root": {
                fontSize: { xs: "1.25rem", sm: "1.5rem" },
                "&.Mui-active": {
                  color: "primary.main",
                },
                "&.Mui-completed": {
                  color: "primary.main",
                },
              },
            }}
          >
            {steps.map((step, index) => (
              <Step
                key={step.label}
                sx={{
                  cursor: step.onClickStep
                    ? "pointer !important"
                    : "default !important",
                }}
                onClick={() => {
                  if (step.onClickStep) {
                    step.onClickStep();
                  }
                }}
              >
                <StepLabel>
                  <Typography
                    variant="caption"
                    color={index <= activeStep ? "primary" : "text.secondary"}
                    sx={{
                      fontWeight: index === activeStep ? 600 : 400,
                      fontSize: { xs: "0.625rem", sm: "0.75rem" },
                      textAlign: "center",
                      lineHeight: 1.2,
                      display: "block",
                      mt: { xs: 0.5, sm: 1 },
                    }}
                  >
                    {step.label}
                  </Typography>
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        {/* Content Section */}
        <Paper
          elevation={1}
          sx={{
            p: { xs: 2, sm: 3, md: 4 },
            borderRadius: 2,
            minHeight: { xs: "300px", sm: "400px" },
          }}
        >
          {steps[activeStep]?.component}
        </Paper>
      </Box>
    </Container>
  );
};
